import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  useTheme,
} from '@mui/material';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import {
  createActivityNameMap,
  getActivities,
} from '../../services/activityService';
import {
  createTrainingClassMap,
  getTrainingClasses,
} from '../../services/trainingClassService';
import { getTrainingSessions } from '../../services/trainingSessionService';
import DashboardChartCard from './DashboardChartCard';
import {
  ACTIVITY_CATEGORY_LINE_DEFAULT_LIMIT,
  buildActivityCategoryTrainingByPeriod,
  buildActivityTotals,
  buildClassTrainingStackByPeriod,
  buildClassTotals,
  buildTotalTrainingByPeriod,
  createActivityCategoryMap,
  durationOptions,
  filterSessionsByDuration,
  isMonthlyGroupingDisabled,
} from './dashboardOverviewUtils';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

function DashboardOverview({
  period,
  duration,
  setPeriod,
  setDuration,
}) {
  const [sessions, setSessions] = useState([]);
  const [activityNamesById, setActivityNamesById] = useState({});
  const [activityCategoriesById, setActivityCategoriesById] = useState({});
  const [selectedActivityCategories, setSelectedActivityCategories] = useState([]);
  const [hasCustomizedActivityCategories, setHasCustomizedActivityCategories] = useState(false);
  const [trainingClassesByName, setTrainingClassesByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken, logout } = useAuth();
  const theme = useTheme();
  const chartColors = useMemo(
    () => theme.customTokens?.chartPalette || [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      '#2a9d8f',
      '#e9c46a',
      '#f4a261',
      '#e76f51',
      '#64748b',
    ],
    [theme]
  );

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      logout({ shouldCallApi: false });
      return;
    }

    const loadDashboardData = async () => {
      try {
        const [sessionsData, activitiesData, trainingClassesData] = await Promise.all([
          getTrainingSessions(accessToken),
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);

        const activities = Array.isArray(activitiesData) ? activitiesData : [];
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setActivityNamesById(createActivityNameMap(activities));
        setActivityCategoriesById(createActivityCategoryMap(activities));
        setTrainingClassesByName(createTrainingClassMap(Array.isArray(trainingClassesData) ? trainingClassesData : []));
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }

        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [accessToken, logout]);

  const filteredSessions = useMemo(
    () => filterSessionsByDuration(sessions, duration),
    [duration, sessions]
  );
  const totalTrainingByPeriod = useMemo(
    () => buildTotalTrainingByPeriod(filteredSessions, period),
    [filteredSessions, period]
  );
  const classTotals = useMemo(
    () => buildClassTotals(filteredSessions, trainingClassesByName),
    [filteredSessions, trainingClassesByName]
  );
  const classTrainingStackByPeriod = useMemo(
    () => buildClassTrainingStackByPeriod(filteredSessions, period, trainingClassesByName),
    [filteredSessions, period, trainingClassesByName]
  );
  const activityTotals = useMemo(
    () => buildActivityTotals(filteredSessions, activityNamesById),
    [activityNamesById, filteredSessions]
  );
  const activityCategoryTrainingByPeriod = useMemo(
    () => buildActivityCategoryTrainingByPeriod(filteredSessions, period, activityCategoriesById),
    [activityCategoriesById, filteredSessions, period]
  );
  const activityCategoryOptions = useMemo(
    () => activityCategoryTrainingByPeriod.categories.map(([categoryName]) => categoryName),
    [activityCategoryTrainingByPeriod.categories]
  );
  const defaultActivityCategories = useMemo(
    () => activityCategoryOptions.slice(0, ACTIVITY_CATEGORY_LINE_DEFAULT_LIMIT),
    [activityCategoryOptions]
  );

  useEffect(() => {
    if (activityCategoryOptions.length === 0) {
      setSelectedActivityCategories([]);
      return;
    }

    setSelectedActivityCategories((currentCategories) => {
      if (!hasCustomizedActivityCategories) {
        return defaultActivityCategories;
      }

      const availableCategories = new Set(activityCategoryOptions);
      const retainedCategories = currentCategories.filter((category) => availableCategories.has(category));
      return retainedCategories.length > 0 ? retainedCategories : defaultActivityCategories;
    });
  }, [activityCategoryOptions, defaultActivityCategories, hasCustomizedActivityCategories]);

  const selectedActivityCategorySet = useMemo(
    () => new Set(selectedActivityCategories),
    [selectedActivityCategories]
  );
  const selectedActivityCategorySeries = useMemo(
    () => activityCategoryTrainingByPeriod.categories
      .filter(([categoryName]) => selectedActivityCategorySet.has(categoryName)),
    [activityCategoryTrainingByPeriod.categories, selectedActivityCategorySet]
  );

  const baseChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${Number(context.raw) || 0} min`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          callback: (value) => `${value}m`,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
    },
  }), [theme]);

  const lineOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: false },
    },
  }), [baseChartOptions]);

  const multiLineOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
  }), [baseChartOptions, theme]);

  const barOptions = useMemo(() => ({
    ...baseChartOptions,
    indexAxis: 'y',
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: false },
    },
    scales: {
      ...baseChartOptions.scales,
      x: {
        ...baseChartOptions.scales.x,
        beginAtZero: true,
        ticks: {
          ...baseChartOptions.scales.x.ticks,
          callback: (value) => `${value}m`,
        },
      },
      y: {
        ...baseChartOptions.scales.y,
        ticks: {
          ...baseChartOptions.scales.y.ticks,
          autoSkip: false,
          callback: (value) => {
            const label = classTotals.labels[value] || '';
            return label.length > 28 ? `${label.slice(0, 25)}...` : label;
          },
        },
        grid: {
          display: false,
        },
      },
    },
  }), [baseChartOptions, classTotals.labels]);

  const stackedBarOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
    scales: {
      ...baseChartOptions.scales,
      x: {
        ...baseChartOptions.scales.x,
        stacked: true,
        title: {
          display: true,
          text: period === 'month' ? 'Month' : 'Week',
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
      },
      y: {
        ...baseChartOptions.scales.y,
        stacked: true,
        title: {
          display: false,
          text: 'Minutes trained',
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
      },
    },
  }), [baseChartOptions, period, theme]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.label}: ${Number(context.raw) || 0} min`,
        },
      },
    },
    cutout: '62%'
  }), [theme]);

  const classPieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const value = Number(context.raw) || 0;
            const total = context.dataset.data.reduce((sum, item) => sum + (Number(item) || 0), 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${context.label}: ${percentage}% (${value} min)`;
          },
        },
      },
    },
  }), [theme]);

  const totalTrainingLineData = useMemo(() => ({
    labels: totalTrainingByPeriod.labels,
    datasets: [
      {
        label: 'Training minutes',
        data: totalTrainingByPeriod.values,
        borderColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        backgroundColor: theme.customTokens?.lineFill || theme.palette.primary.main,
        tension: 0.28,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
      },
    ],
  }), [theme, totalTrainingByPeriod.labels, totalTrainingByPeriod.values]);

  const classBarData = useMemo(() => ({
    labels: classTotals.labels,
    datasets: [
      {
        label: 'Training minutes',
        data: classTotals.values,
        backgroundColor: classTotals.values.map((_, index) => chartColors[index % chartColors.length]),
        borderRadius: 6,
      },
    ],
  }), [chartColors, classTotals.labels, classTotals.values]);

  const classStackedBarData = useMemo(() => ({
    labels: classTrainingStackByPeriod.labels,
    datasets: classTrainingStackByPeriod.classes.map(([className, totalsByPeriod], index) => ({
      label: className,
      data: classTrainingStackByPeriod.periodKeys.map((periodKey) => totalsByPeriod[periodKey] || 0),
      backgroundColor: chartColors[index % chartColors.length],
      borderRadius: (context) => {
        const dataIndex = context.dataIndex;
        let topDatasetIndex = -1;

        for (let datasetIndex = context.chart.data.datasets.length - 1; datasetIndex >= 0; datasetIndex -= 1) {
          const dataset = context.chart.data.datasets[datasetIndex];
          if (
            context.chart.isDatasetVisible(datasetIndex)
            && (Number(dataset.data[dataIndex]) || 0) > 0
          ) {
            topDatasetIndex = datasetIndex;
            break;
          }
        }

        return context.datasetIndex === topDatasetIndex
          ? { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 }
          : 0;
      },
      borderSkipped: false,
    })),
  }), [
    chartColors,
    classTrainingStackByPeriod.classes,
    classTrainingStackByPeriod.labels,
    classTrainingStackByPeriod.periodKeys,
  ]);

  const classPieData = useMemo(() => ({
    labels: classTotals.labels,
    datasets: [
      {
        label: 'Class training share',
        data: classTotals.values,
        backgroundColor: classTotals.values.map((_, index) => chartColors[index % chartColors.length]),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  }), [chartColors, classTotals.labels, classTotals.values, theme.palette.background.paper]);

  const activityPieData = useMemo(() => ({
    labels: activityTotals.labels,
    datasets: [
      {
        label: 'Activity minutes',
        data: activityTotals.values,
        backgroundColor: activityTotals.values.map((_, index) => chartColors[index % chartColors.length]),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  }), [activityTotals.labels, activityTotals.values, chartColors, theme.palette.background.paper]);

  const activityMultiLineData = useMemo(() => ({
    labels: activityCategoryTrainingByPeriod.labels,
    datasets: selectedActivityCategorySeries.map(([categoryName, totalsByPeriod], index) => ({
      label: categoryName,
      data: activityCategoryTrainingByPeriod.periodKeys.map((periodKey) => totalsByPeriod[periodKey] || 0),
      borderColor: chartColors[index % chartColors.length],
      backgroundColor: chartColors[index % chartColors.length],
      tension: 0.25,
      pointRadius: 2,
      pointHoverRadius: 4,
    })),
  }), [
    activityCategoryTrainingByPeriod.labels,
    activityCategoryTrainingByPeriod.periodKeys,
    chartColors,
    selectedActivityCategorySeries,
  ]);

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="dashboard-overview">
      {error && (
        <Alert severity="error" className="dashboard-error">
          {error}
        </Alert>
      )}

      <Paper className="dashboard-filter-bar" elevation={0}>
        <FormControl className="dashboard-filter-control" size="small">
          <InputLabel id="dashboard-period-label">Grouping</InputLabel>
          <Select
            labelId="dashboard-period-label"
            value={period}
            label="Grouping"
            onChange={(event) => setPeriod(event.target.value)}
          >
            <MenuItem value="week">Weekly</MenuItem>
            <MenuItem value="month" disabled={isMonthlyGroupingDisabled(duration)}>
              Monthly
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl className="dashboard-filter-control" size="small">
          <InputLabel id="dashboard-duration-label">Duration</InputLabel>
          <Select
            labelId="dashboard-duration-label"
            value={duration}
            label="Duration"
            onChange={(event) => setDuration(event.target.value)}
          >
            {durationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Box className="dashboard-chart-grid">
        <DashboardChartCard
          title="Training Over Time"
          isEmpty={totalTrainingByPeriod.values.length === 0}
          emptyMessage="No training data found for this duration."
          cardClassName="dashboard-chart-card-wide-left"
        >
          <Line data={totalTrainingLineData} options={lineOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title="Training Minutes by Class"
          isEmpty={classTotals.values.length === 0}
          emptyMessage="No class duration data found for this duration."
          cardClassName="dashboard-chart-card-narrow-right"
          chartFrameClassName="dashboard-horizontal-bar-frame"
        >
          <Bar data={classBarData} options={barOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title="Activity Duration Minutes"
          isEmpty={activityTotals.values.length === 0}
          emptyMessage="No activity duration data found for this duration."
          cardClassName="dashboard-chart-card-narrow-left"
        >
          <Doughnut data={activityPieData} options={pieOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title="Activity Category Over Time"
          isEmpty={activityCategoryTrainingByPeriod.categories.length === 0 || selectedActivityCategorySeries.length === 0}
          emptyMessage={
            activityCategoryTrainingByPeriod.categories.length === 0
              ? 'No activity category timeline data found for this duration.'
              : 'Select at least one category to show this timeline.'
          }
          cardClassName="dashboard-chart-card-wide-right"
          action={(
            <FormControl className="dashboard-category-filter" size="small">
              <InputLabel id="dashboard-category-filter-label">Categories</InputLabel>
              <Select
                labelId="dashboard-category-filter-label"
                multiple
                value={selectedActivityCategories}
                label="Categories"
                renderValue={(selected) => `${selected.length} selected`}
                onChange={(event) => {
                  const value = event.target.value;
                  setHasCustomizedActivityCategories(true);
                  setSelectedActivityCategories(
                    typeof value === 'string' ? value.split(',') : value
                  );
                }}
              >
                {activityCategoryOptions.map((categoryName) => (
                  <MenuItem key={categoryName} value={categoryName}>
                    <Checkbox checked={selectedActivityCategories.includes(categoryName)} />
                    <ListItemText primary={categoryName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        >
          <Line data={activityMultiLineData} options={multiLineOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title={`Training Time by ${period === 'month' ? 'Month' : 'Week'}`}
          isEmpty={classTrainingStackByPeriod.classes.length === 0}
          emptyMessage="No class duration timeline data found for this duration."
          cardClassName="dashboard-chart-card-wide-left"
        >
          <Bar data={classStackedBarData} options={stackedBarOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title="Training Time By Class"
          isEmpty={classTotals.values.length === 0}
          emptyMessage="No class duration data found for this duration."
          cardClassName="dashboard-chart-card-narrow-right"
        >
          <Pie data={classPieData} options={classPieOptions} />
        </DashboardChartCard>
      </Box>
    </Box>
  );
}

export default DashboardOverview;
